import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CrystallisationOption } from "../../../../../../../Wrap/Constants/Enums/Drawdown/CrystallisationOption";
import { CrystallisationDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/CrystallisationDetails";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { SectionHeader } from "../../SharedComponents";
import {
    calculateUnCrystallizationAmount,
    hasProtectedRetireAgeOrSchemeTaxFreeCash,
} from "./Helper/DrawdownDetailHelper";
import {
    CrystallisationOptions,
    drawdownDetailsMessage,
    crystallisationAmountErrorMessage,
    CrystallisationOptionsMap,
} from "./Resource/Contents";

interface ICrystallisationDetailsProps extends IProcessComponentProps {
    data: DrawdownModel;
    pensionProtection: PensionProtectionDetails;
    processType: ProcessType;
    modelData: PensionBase;
    cashInstrmentProductId: number;
    doCalculation: VoidFunction;
    isImmediateTaxFreeLumpSum: boolean;
}

interface ICrystallisationDetailsState {}

export class CrystallisationDetailsSection extends ProcessComponent<
    ICrystallisationDetailsProps,
    ICrystallisationDetailsState
> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        super.componentDidMount();
        const { modelData, data, isImmediateTaxFreeLumpSum, processType } = this.props;
        const fullAmount = calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum);
        this.onCrystallisationDetailChange({
            CrystallisationOption: !this.props.data.CrystallisationDetails?.CrystallisationOption
                ? this.getCrystallisationOptionDefaultValue(this.props.data.CrystallisationDetails, fullAmount)
                : data?.CrystallisationDetails?.CrystallisationOption,
            CrystallisationAmount: isImmediateTaxFreeLumpSum
                ? { Amount: fullAmount, Currency: CurrencyCode.GBP }
                : this.props.data.CrystallisationDetails?.CrystallisationAmount,
            // UnCrystallisedValue: { Amount: fullAmount, Currency: CurrencyCode.GBP },
        });
    }

    private onCrystallisationDetailChange(changes) {
        this.onDataChange(this.props.onChange, changes, this.props.data, "CrystallisationDetails");
    }

    private getConfigTable = (CrystallisationDetails: CrystallisationDetails) => {
        const { modelData, processType, isImmediateTaxFreeLumpSum } = this.props;
        let config = [
            {
                title: "Uncrystallised value",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum),
                    Currency: CurrencyCode.GBP,
                }),
            },
            {
                title: "Value available to crystallise",
                data: MoneyDisplayHelper.getMoneyFormat(CrystallisationDetails?.AvailableToCrystallise),
            },
            {
                title: "Available cash",
                data: MoneyDisplayHelper.getMoneyFormat(CrystallisationDetails?.AvailableCash),
            },
        ];
        if (![ProcessType.NB, ProcessType.Quote].includes(this.props.processType)) {
            config = [
                {
                    title: "Crystallised value",
                    data: MoneyDisplayHelper.getMoneyFormat(CrystallisationDetails?.CrystallisedValue),
                },
            ].concat(config);
        }
        return config;
    };

    private renderCrystallisationDetails(): JSX.Element {
        const { data, pensionProtection, isImmediateTaxFreeLumpSum, modelData, processType } = this.props;
        const { CrystallisationDetails } = data;
        const isUFPLS = data?.PensionOption == PensionOption.UFPLS;
        return (
            <>
                <Typography type="common" wrapCss="dt-bold" config={this.getConfigTable(CrystallisationDetails)} />
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label="Crystallisation options"
                    disabled={
                        CrystallisationDetails?.AvailableToCrystallise?.Amount <
                        calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                    }
                    options={CrystallisationOptions}
                    getText={(v) => {
                        return CrystallisationOptionsMap[v];
                    }}
                    selected={CrystallisationDetails?.CrystallisationOption}
                    onChange={(v) => {
                        this.onCrystallisationDetailChange({
                            CrystallisationOption: v,
                            CrystallisationAmount: {
                                Amount:
                                    v == CrystallisationOption.Full
                                        ? CrystallisationDetails.AvailableToCrystallise?.Amount
                                        : 0,
                                Currency: CurrencyCode.GBP,
                            },
                        });
                    }}
                    onBlur={(v) => this.props.doCalculation()}
                    alwaysValidateOnUpdate
                    validationRules={[
                        {
                            errorMessage: drawdownDetailsMessage.crystallisationOptionErrorMessage,
                            validator: (v) => {
                                return v == CrystallisationOption.Full
                                    ? true
                                    : v == CrystallisationOption.Partial &&
                                          !hasProtectedRetireAgeOrSchemeTaxFreeCash(pensionProtection);
                            },
                        },
                        {
                            errorMessage: drawdownDetailsMessage.crystallisationOptionFullErrorMessage,
                            validator: (v) => {
                                return v == CrystallisationOption.Partial
                                    ? true
                                    : v == CrystallisationOption.Full &&
                                          this.validateFullOption(
                                              isUFPLS,
                                              CrystallisationDetails,
                                              modelData,
                                              processType,
                                              isImmediateTaxFreeLumpSum
                                          );
                            },
                        },
                    ]}
                />
                <MoneyTextBoxEditor
                    cssWrapper="control-size-245"
                    label="Crystallisation amount"
                    currency={CurrencyCode.GBP}
                    value={CrystallisationDetails?.CrystallisationAmount?.Amount}
                    disabled={CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Full}
                    required={"Please enter"}
                    onBlur={(v) => this.props.doCalculation()}
                    onChange={(v) =>
                        this.onCrystallisationDetailChange({
                            CrystallisationAmount: { Amount: v, Currency: CurrencyCode.GBP },
                        })
                    }
                    validationRules={[
                        {
                            errorMessage: crystallisationAmountErrorMessage.valueToCrystalliseIsZero,
                            validator: (v) => CrystallisationDetails?.AvailableToCrystallise?.Amount != 0,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputIsZeroAndLessThanUncrystallised,
                            validator: (v) =>
                                v == 0 &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount <
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputIsZeroAndEqualUncrystallised,
                            validator: (v) =>
                                v == 0 &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount ==
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputEqualValueToCrystallise,
                            validator: (v) =>
                                v == CrystallisationDetails?.AvailableToCrystallise?.Amount &&
                                CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Partial &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount ==
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndLessThanUncrystallised,
                            validator: (v) =>
                                v > CrystallisationDetails?.AvailableToCrystallise?.Amount &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount <
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndEqualUncrystallised,
                            validator: (v) =>
                                v > CrystallisationDetails?.AvailableToCrystallise?.Amount &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount ==
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndLessThanUncrystallised,
                            validator: (v) =>
                                v >
                                    Math.min(
                                        CrystallisationDetails?.AvailableToCrystallise?.Amount,
                                        CrystallisationDetails?.AvailableCash.Amount
                                    ) &&
                                data.PensionOption &&
                                CrystallisationDetails?.AvailableToCrystallise?.Amount <
                                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                                    ? false
                                    : true,
                        },
                    ]}
                />
            </>
        );
    }

    private validateFullOption(
        isUFPLS: boolean,
        data: CrystallisationDetails,
        modelData: PensionBase,
        processType: ProcessType,
        isImmediateTaxFreeLumpSum: boolean
    ): boolean {
        if (isUFPLS) {
            return (
                data?.AvailableToCrystallise?.Amount ==
                    calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum) &&
                calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum) ==
                    data?.AvailableCash?.Amount
            );
        } else {
            return (
                data?.AvailableToCrystallise?.Amount ==
                calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
            );
        }
    }

    private getCrystallisationOptionDefaultValue(data: CrystallisationDetails, amount: number): CrystallisationOption {
        if (this.props.isImmediateTaxFreeLumpSum) {
            return amount == data?.AvailableToCrystallise?.Amount
                ? CrystallisationOption.Full
                : CrystallisationOption.Partial;
        }
        if (data?.AvailableToCrystallise?.Amount < amount) {
            return CrystallisationOption.Partial;
        }
        return null;
    }

    public render(): JSX.Element {
        return (
            <>
                {SectionHeader("Crystallisation details")}
                {this.renderCrystallisationDetails()}
            </>
        );
    }
}
